import React, { useState } from "react";
import { BaseImportExportModalComponent } from "../../../components/modals";
import { DataTypes } from "../../../shared";
import { getRegionFromURL } from "../../../shared/utils/region";
import {
  getOpeningBalancesSpecificDescription,
  getTimeFeesDisbursementsRegionSpecificLabel,
  PRODUCT_TIERS,
} from "../../../shared/utils/constants";
import log from "../../../shared/utils/log";

export interface ImportOption {
  label: string;
  description?: string;
  selected: boolean;
  dataTypes: string[];
}

export const ImportTypeSelectionModal: React.FC<{
  closeModal: () => void;
  onConfirm: (selectedOptions: string[]) => void;
  productId: string | undefined;
  titleText: string;
  subtitleText: string;
  existingDataTypes: string[];
}> = ({
  closeModal,
  onConfirm,
  productId,
  titleText,
  subtitleText,
  existingDataTypes,
}) => {
  const region = getRegionFromURL();
  const [dataTypesToShow, setDataTypesToShow] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isGrowOrProsper =
    productId === PRODUCT_TIERS.GROW || productId === PRODUCT_TIERS.PROSPER;

  const [options, setOptions] = useState<ImportOption[]>(() => {
    // Initialise options with region and tier logic
    let initialOptions: ImportOption[] = [
      {
        label: DataTypes.Staff,
        selected: false,
        dataTypes: [DataTypes.Staff],
      },
      {
        label: DataTypes.Contacts,
        selected: false,
        dataTypes: [DataTypes.Contacts],
      },
      {
        label: DataTypes.Matters,
        selected: false,
        dataTypes: [DataTypes.Matters, DataTypes.MatterTypes],
      },
      ...(isGrowOrProsper
        ? [
            {
              label: DataTypes.MatterTags,
              selected: false,
              dataTypes: [DataTypes.MatterTags],
            },
            {
              label: "Matter Roles",
              selected: false,
              dataTypes: [DataTypes.MatterRelationships],
            },
            {
              label: DataTypes.MatterCriticalDates,
              selected: false,
              dataTypes: [DataTypes.MatterCriticalDates],
            },
            {
              label: DataTypes.MatterArchiveDetails,
              selected: false,
              dataTypes: [DataTypes.MatterArchiveDetails],
            },
          ]
        : []),
      {
        label: DataTypes.OpeningBalances,
        description: getOpeningBalancesSpecificDescription(region),
        selected: false,
        dataTypes: [DataTypes.OpeningBalances],
      },
      {
        label: getTimeFeesDisbursementsRegionSpecificLabel(region),
        description: "Includes Activity Types",
        selected: false,
        dataTypes: [
          DataTypes.ActivityTypes,
          DataTypes.UnbilledTimeAndFees,
          DataTypes.UnbilledDisbursements,
        ],
      },
      {
        label: DataTypes.Invoices,
        selected: false,
        dataTypes: [DataTypes.Invoices],
      },
    ];

    // Additional types available for Boost, Grow, and Prosper product tiers only
    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      initialOptions.push({
        label: DataTypes.Memos,
        selected: false,
        dataTypes: [DataTypes.Memos],
      });
      initialOptions.push({
        label: DataTypes.Tasks,
        selected: false,
        dataTypes: [DataTypes.Tasks],
      });
      initialOptions.push({
        label: "Calendar Events",
        selected: false,
        dataTypes: [DataTypes.Events],
      });
    }

    // Add Safe Custody Packets and Documents only for Grow/Prosper tiers and non-US regions
    if (isGrowOrProsper && region !== "US") {
      initialOptions.push({
        label: "Safe Custody Packets and Documents",
        selected: false,
        dataTypes: [
          DataTypes.SafeCustodyPackets,
          DataTypes.SafeCustodyDocuments,
        ],
      });
    }

    // Filter out existing import types, including grouped ones
    return initialOptions.filter((option) => {
      // Check if any of the option's `importTypes` are in the existingDataTypes
      return !option.dataTypes.every((dataType) =>
        existingDataTypes.includes(dataType),
      );
    });
  });

  const handleOptionChange = (index: number) => {
    const newOptions = [...options];
    newOptions[index].selected = !newOptions[index].selected;

    const selectedOption = newOptions[index];

    // If the option is selected, add its cards to the list
    if (selectedOption.selected) {
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = [...prevDataTypes];
        selectedOption.dataTypes.forEach((dataType) => {
          if (!newDataTypes.includes(dataType)) {
            newDataTypes.push(dataType); // Add the card if it's not already in the list
          }
        });
        return newDataTypes;
      });
    } else {
      // If the option is deselected, check if any other selected options require this option's import types
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = prevDataTypes.filter(
          (dataType) =>
            !selectedOption.dataTypes.includes(dataType) ||
            // Keep import types that are still required by other selected options
            newOptions.some(
              (option) =>
                option.selected && option.dataTypes.includes(dataType),
            ),
        );
        return newDataTypes;
      });
    }
    setOptions(newOptions);
  };

  const handleUnselectAll = () => {
    setOptions(options.map((option) => ({ ...option, selected: false })));
    setDataTypesToShow([]); // Clear import types when unselecting all
  };

  const handleSelectAll = () => {
    // Always show these import types for all tiers
    const defaultDataTypes = [
      DataTypes.Staff,
      DataTypes.Contacts,
      DataTypes.Matters,
      DataTypes.MatterTypes,
      ...(isGrowOrProsper
        ? [DataTypes.MatterTags, DataTypes.MatterRelationships, DataTypes.MatterCriticalDates, DataTypes.MatterArchiveDetails]
        : []),
      DataTypes.OpeningBalances,
      DataTypes.ActivityTypes,
      DataTypes.UnbilledTimeAndFees,
      DataTypes.UnbilledDisbursements,
      DataTypes.Invoices,
    ];

    dataTypesToShow.push(...defaultDataTypes);

    // Add region and tier specific imports
    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      dataTypesToShow.push(DataTypes.Memos, DataTypes.Tasks, DataTypes.Events);
    }

    if (
      (productId === PRODUCT_TIERS.GROW ||
        productId === PRODUCT_TIERS.PROSPER) &&
      region !== "US"
    ) {
      dataTypesToShow.push(
        DataTypes.SafeCustodyPackets,
        DataTypes.SafeCustodyDocuments,
      );
    }

    setOptions(options.map((option) => ({ ...option, selected: true })));
    setDataTypesToShow(dataTypesToShow);
  };

  const handleProceed = async () => {
    setIsLoading(true);
    try {
      // Deduplicate the import types to ensure no duplicates
      const uniqueDataTypes = dataTypesToShow.filter((type, index, self) => {
        return self.indexOf(type) === index;
      });
      log("Final unique import types: ", uniqueDataTypes);

      onConfirm(uniqueDataTypes); // Pass deduplicated string array to parent
      closeModal(); // Close the modal after proceeding
    } catch (error) {
      console.error("Error during import confirmation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseImportExportModalComponent
      titleText={titleText}
      subtitleText={subtitleText}
      closeModal={closeModal}
      isLoading={isLoading}
      options={options}
      onToggleOption={handleOptionChange}
      onSelectAll={handleSelectAll}
      onUnselectAll={handleUnselectAll}
      onProceed={handleProceed}
    />
  );
};
